import * as React from "react";
import { useForm } from "react-hook-form";
import {
    Button,
    FormButton,
    Popover,
    PopoverContent,
    PopoverTrigger,
    SearchInput,
} from "@jhool-io/fe-components";
import { child, push, ref, update } from "firebase/database";
import { useSearchParams } from "react-router-dom";
import { ICreateThirtydaysProviderPayload } from "../../types/thirtydays-providers";
import useToast from "../../../../hooks/useToast";
import { rtdb } from "../../../../firebase";
import { useFetchProviders } from "../../../providers/hooks/providers.queries";
import { ProviderStatus } from "../../../providers/types/providers.types";
import { useDebounce } from "../../../../hooks/helpers";
import { useFetchUserDetails } from "../../../../hooks/queries/user";

import ListState from "../../../../components/ListState/ListState";
import Loader from "../../../../shared-ui/Loader/Loader";
import { useAppSelector } from "../../../../hooks/useRedux";

interface NewThirtydaysProviderFormProps {
    onFormSubmit: () => void;
}

type ComboOption = {
    value: string;
    label: string;
};

export default function NewThirtydaysProviderForm({
    onFormSubmit,
}: NewThirtydaysProviderFormProps) {
    const [searchParams, setSearchParams] = useSearchParams();

    const [providerSearchString, setProviderSearchString] = React.useState("");
    const [selectedProvider, setSelectedProvider] =
        React.useState<ComboOption | null>(null);
    const [showProvidersListPopover, setShowProvidersListPopover] =
        React.useState(false);

    const debouncedProviderSearchString = useDebounce(
        providerSearchString,
        500
    );

    const providersListContainerRef = React.useRef<HTMLDivElement>(null);

    const { data, isLoading, error, isSuccess } = useFetchProviders({
        page: 1,
        limit: 20,
        status: ProviderStatus.ACTIVE,
        search_string: debouncedProviderSearchString,
    });

    const loggedInUser = useFetchUserDetails();

    const { practice } = useAppSelector((state) => state.userPractice);

    const { toast } = useToast();

    const { handleSubmit } = useForm<ICreateThirtydaysProviderPayload>({});

    const onSubmit = async () => {
        if (!selectedProvider) {
            toast({ mode: "error", message: "Provider field cannot be empty" });
            return;
        }
        searchParams.set("adding_provider", "true");
        setSearchParams(searchParams);

        const newProviderKey = push(
            child(ref(rtdb), "thirtydaysproviders")
        ).key;

        try {
            await update(
                ref(
                    rtdb,
                    `/thirtydaysproviders/${practice?.practice_id}/${newProviderKey}`
                ),
                {
                    provider_name: selectedProvider?.label,
                    provider_id: selectedProvider?.value,
                    created_at: new Date().toISOString(),
                    created_by: {
                        user_id: loggedInUser?.data?.user_id || "",
                        first_name: loggedInUser?.data?.first_name || "",
                        last_name: loggedInUser?.data?.last_name || "",
                    },
                }
            );

            toast({
                mode: "success",
                message: "Provider added successfully",
            });

            onFormSubmit();
        } catch {
            toast({
                mode: "error",
                message:
                    "Could not add provider at this time, please try again later",
            });
            searchParams.delete("adding_provider");
            setSearchParams(searchParams);
        }
    };

    return (
        <form id="add-provider" onSubmit={handleSubmit(onSubmit)}>
            <div className="flex-1" ref={providersListContainerRef}>
                <Popover
                    open={showProvidersListPopover}
                    onOpenChange={setShowProvidersListPopover}
                >
                    <PopoverTrigger
                        asChild
                        onClick={() => setShowProvidersListPopover(true)}
                        className="w-full"
                    >
                        <FormButton
                            label="Select a provider"
                            value={selectedProvider?.label ?? ""}
                            placeholder="Select a provider"
                        />
                    </PopoverTrigger>

                    <PopoverContent
                        className="popover-full"
                        align="start"
                        container={providersListContainerRef.current}
                    >
                        <SearchInput
                            placeholder="Search by provider name"
                            onChange={(e) =>
                                setProviderSearchString(e.target.value)
                            }
                        />

                        <div className="flex flex-col items-start max-h-[230px] overflow-y-auto">
                            {isLoading && (
                                <Loader wrapperClassName="h-[150px]" />
                            )}

                            {error && (
                                <ListState
                                    isError
                                    context="general"
                                    stateHelperText="Please try again later"
                                    errorMsg={
                                        error?.response?.data?.message ||
                                        "Error loading providers"
                                    }
                                    cardClass="h-[150px]"
                                />
                            )}

                            {data?.data && isSuccess && (
                                <>
                                    <Button
                                        size="auto"
                                        variant="can-hover"
                                        className="font-medium capitalize w-full justify-start my-1.5 shrink-0"
                                        onClick={() => {
                                            setProviderSearchString("");
                                            setSelectedProvider(null);
                                            setShowProvidersListPopover(false);
                                        }}
                                    >
                                        Clear selected
                                    </Button>
                                    {data?.data.map((provider) => (
                                        <Button
                                            key={provider.provider_id}
                                            size="auto"
                                            variant="can-hover"
                                            className="font-medium capitalize w-full justify-start my-1 shrink-0"
                                            onClick={() => {
                                                setSelectedProvider({
                                                    label: provider.provider_name,
                                                    value: provider.provider_id,
                                                });
                                                setShowProvidersListPopover(
                                                    false
                                                );
                                            }}
                                        >
                                            {provider.provider_name}
                                        </Button>
                                    ))}
                                </>
                            )}
                        </div>
                    </PopoverContent>
                </Popover>
            </div>
        </form>
    );
}
